// src/utils/titleVendors.js
// Team Edelstein's PREFERRED TITLE VENDORS list — the pickable options in the
// turn-in title dropdown. Stored as a single settings doc holding an array;
// seeded from the team-default title company so nothing is lost on first use.

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import { loadTeamDefaults, TEAM_DEFAULTS_FALLBACK } from "./teamDefaults";

const DOC_PATH = ["settings", "titleVendors"];

// Shape of one vendor: { id, company, officerName, email, phone }.
export const TITLE_VENDORS_FALLBACK = [
  {
    id: "seed-default",
    company: TEAM_DEFAULTS_FALLBACK.titleCompany,
    officerName: TEAM_DEFAULTS_FALLBACK.titleCloserName,
    email: TEAM_DEFAULTS_FALLBACK.titleCloserEmail || "",
    phone: TEAM_DEFAULTS_FALLBACK.titleCloserPhone || "",
  },
];

// A short unique id for a new vendor row (app runtime only).
export function newVendorId() {
  return "v" + Date.now() + "-" + Math.floor(Math.random() * 1e6);
}

// Normalize an arbitrary object into a clean vendor row.
export function toVendor(v = {}) {
  return {
    id: v.id || newVendorId(),
    company: (v.company || "").trim(),
    officerName: (v.officerName || "").trim(),
    email: (v.email || "").trim(),
    phone: (v.phone || "").trim(),
  };
}

// Load the saved list. If none exists yet, seed one row from the live team
// default title company (falls back to the static Watermark Title default).
export async function loadTitleVendors() {
  try {
    const snap = await getDoc(doc(firestored, ...DOC_PATH));
    const arr = snap.exists() ? snap.data().vendors : null;
    if (Array.isArray(arr) && arr.length) return arr.map(toVendor);
  } catch (err) {
    console.error("loadTitleVendors failed:", err);
  }
  try {
    const td = await loadTeamDefaults();
    return [
      toVendor({
        id: "seed-default",
        company: td.titleCompany || TEAM_DEFAULTS_FALLBACK.titleCompany,
        officerName: td.titleCloserName || TEAM_DEFAULTS_FALLBACK.titleCloserName,
        email: td.titleCloserEmail || "",
        phone: td.titleCloserPhone || "",
      }),
    ];
  } catch {
    return TITLE_VENDORS_FALLBACK.map(toVendor);
  }
}

// Overwrite the whole list (used by the Settings tab: add / edit / remove).
export async function saveTitleVendors(vendors, userId) {
  const clean = (vendors || []).map(toVendor).filter((v) => v.company);
  await setDoc(
    doc(firestored, ...DOC_PATH),
    { vendors: clean, updatedAt: serverTimestamp(), updatedBy: userId || "" },
    { merge: true },
  );
  return clean;
}

// Append one vendor to the shared list (used by "Add new" on the turn-in, which
// any agent may do). Re-reads first so a concurrent add isn't clobbered.
export async function addTitleVendor(vendor, userId) {
  const current = await loadTitleVendors();
  const added = toVendor(vendor);
  const next = [...current, added];
  await saveTitleVendors(next, userId);
  return { vendor: added, vendors: next };
}
